import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, UsersIcon, BadgeDollarSignIcon } from 'lucide-react';
const HERO_IMG_DESKTOP = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/Founders_command_center_16_9.jpg?v=1777944210';
const HERO_IMG_MOBILE = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/Founders_command_center_3_4.jpg?v=1777944210';
const scrollTo = (id: string) => document.querySelector(id)?.scrollIntoView({
  behavior: 'smooth'
});
export function HeroSection() {
  return <section className="relative bg-[#060B18] overflow-hidden" data-id="element-145">
      <div className="absolute top-1/3 left-1/4 w-[700px] h-[700px] bg-teal/8 rounded-full blur-[160px] pointer-events-none" data-id="element-146" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#FFB800]/6 rounded-full blur-[120px] pointer-events-none" data-id="element-147" />
      <div className="absolute top-0 right-1/3 w-[400px] h-[400px] bg-purple/5 rounded-full blur-[100px] pointer-events-none" data-id="element-148" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-20 pb-0" data-id="element-149">

        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex justify-center mb-5" data-id="element-150">
          <span className="text-xs font-semibold tracking-widest uppercase text-teal bg-teal/10 px-4 py-1.5 rounded-full border border-teal/20" data-id="element-151">
            Shopify · Etsy · Amazon Sellers
          </span>
        </motion.div>

        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.65,
        delay: 0.08
      }} className="text-center max-w-3xl mx-auto mb-5" data-id="element-152">
          <h1 className="font-display font-extrabold leading-[1.08] mb-5" data-id="element-153">
            <span className="block text-white text-[2.4rem] sm:text-5xl lg:text-6xl" data-id="element-154">
              Track Your Business
            </span>
            <span className="block text-[2.4rem] sm:text-5xl lg:text-6xl" style={{
            background: 'linear-gradient(135deg, #00C9B1, #0084A8)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} data-id="element-155">
              Real Profit
            </span>
            <span className="block text-white/65 text-xl sm:text-2xl lg:text-3xl font-semibold mt-2" data-id="element-156">
              Save time on the tedious tasks that hold you back
            </span>
          </h1>

          <p className="text-white/55 text-base sm:text-lg max-w-xl mx-auto leading-relaxed" data-id="element-157">
            The complete business dashboard built for Shopify, Etsy and Amazon sellers.
            Google Sheets and Excel. No accountant needed.
          </p>
        </motion.div>

        {/* FREE Bonuses banner */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.18
      }} className="flex justify-center mb-5" data-id="element-158">
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl" style={{
          background: 'linear-gradient(135deg, rgba(255,184,0,0.14), rgba(255,107,0,0.08))',
          border: '1.5px solid rgba(255,184,0,0.38)',
          boxShadow: '0 0 32px rgba(255,184,0,0.14)'
        }} data-id="element-159">
            <span className="text-lg flex-shrink-0" data-id="element-160">🎁</span>
            <p className="font-display font-bold text-[#FFB800] text-sm sm:text-base" data-id="element-161">
              Sign Now — Get 2 Free Bonuses Included
            </p>
          </div>
        </motion.div>

        {/* Trust bullets — in Bryams' preferred order */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.28
      }} className="flex flex-wrap gap-3 sm:gap-5 justify-center mb-7" data-id="element-162">
          {[{
          icon: ClockIcon,
          label: '5 Minutes Setup Time'
        }, {
          icon: UsersIcon,
          label: '+100 Happy Customers'
        }, {
          icon: BadgeDollarSignIcon,
          label: '$34 Max Savings on VIP Pack'
        }].map(({
          icon: Icon,
          label
        }) => <div key={label} className="flex items-center gap-2 text-white/50 text-sm" data-id="element-163">
              <Icon className="w-4 h-4 text-teal flex-shrink-0" data-id="element-164" />
              <span data-id="element-165">{label}</span>
            </div>)}
        </motion.div>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.36
      }} className="flex flex-col sm:flex-row gap-3 justify-center mb-10" data-id="element-166">
          <motion.button whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} onClick={() => scrollTo('#upgrades')} className="bg-gradient-gold text-white font-display font-bold text-base sm:text-lg px-8 sm:px-10 py-4 rounded-full hover:shadow-2xl hover:shadow-[#FF6B00]/30 transition-all duration-300" data-id="element-167">
            Get Instant Access Starting at $59.99
          </motion.button>
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => scrollTo('#product')} className="border border-white/22 text-white/80 font-semibold text-base px-8 py-4 rounded-full hover:border-teal/50 hover:text-teal transition-all duration-300" data-id="element-168">
            Explore What's Inside
          </motion.button>
        </motion.div>

        {/* Hero image */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="relative mx-auto max-w-6xl" data-id="element-169">
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.1,
          duration: 0.5
        }} className="absolute top-4 right-4 sm:top-6 sm:right-8 z-10 bg-teal/15 border border-teal/30 backdrop-blur-md text-teal text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg hidden sm:block" data-id="element-170">
            ✓ Real-time profit tracking
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.3,
          duration: 0.5
        }} className="absolute bottom-10 left-4 sm:left-8 z-10 bg-[#FFB800]/15 border border-[#FFB800]/30 backdrop-blur-md text-[#FFB800] text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg hidden sm:block" data-id="element-171">
            📊 Dashboard + Inventory + Invoices
          </motion.div>

          <picture data-id="element-172">
            <source media="(min-width: 640px)" srcSet={HERO_IMG_DESKTOP} data-id="element-173" />
            <img src={HERO_IMG_MOBILE} alt="The Founder's Business Command Center dashboard bundle" className="w-full rounded-t-2xl object-cover object-top shadow-2xl" style={{
            boxShadow: '0 -20px 80px rgba(0,201,177,0.12), 0 40px 80px rgba(0,0,0,0.6)',
            maxHeight: '520px'
          }} loading="eager" data-id="element-174" />
          </picture>
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#060B18] to-transparent rounded-b-2xl pointer-events-none" data-id="element-175" />
        </motion.div>
      </div>
    </section>;
}